import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

/**
 * VoxyConfig — initializes the voxy mic pipeline and applies VAD thresholds.
 *
 * Wire from `beginPlay.execOut` → `execIn`. On first fire, constructs
 * VoxyCore with these thresholds and starts listening; on subsequent fires
 * (or field edits that re-run the chain), calls updateVadConfig live.
 *
 * If this node doesn't exist in the graph, or isn't reachable from an event
 * source, voxy never starts and the mic stays off.
 */

const HEADER_BG     = '#0a3028'
const HEADER_ACTIVE = '#10503a'
const ACCENT        = '#40d090'
const BORDER_SEL    = '#40d090'
const BORDER_IDLE   = '#0d4030'

function Row({ label, value, onChange, step, min, max, placeholder }) {
  const stop = e => e.stopPropagation()
  return (
    <div style={{ padding: '6px 12px 0 12px' }}>
      <div style={{ color: '#1a4030', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>{label}</div>
      <input className={nodeControlClass()}
        type="number"
        data-allow-empty /* blank = unset (null) — see numericScrubInstall */
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        onMouseDown={stop}
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: '#020a06', border: '1px solid #0d3020',
          color: '#60d090', padding: '4px 7px',
          borderRadius: 3, fontSize: 10,
          fontFamily: 'var(--m-mono)', outline: 'none',
        }}
      />
    </div>
  )
}

export function VoxyConfigNode({ data, selected }) {
  const fired = isFired(data)

  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 240, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🎙</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          Voxy Config
        </span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>

      <Row
        label="VAD START THRESHOLD (0..1)"
        value={data.vadThreshold}
        onChange={v => data.onChange?.('vadThreshold', v)}
        step={0.05} min={0} max={1}
        placeholder="0.7"
      />
      <Row
        label="VAD END THRESHOLD (0..1)"
        value={data.vadEndThreshold}
        onChange={v => data.onChange?.('vadEndThreshold', v)}
        step={0.05} min={0} max={1}
        placeholder="0.35"
      />
      <Row
        label="SILENCE TIMEOUT (ms)"
        value={data.silenceTimeoutMs}
        onChange={v => data.onChange?.('silenceTimeoutMs', v)}
        step={50} min={0}
        placeholder="800"
      />
      <Row
        label="MIN SPEECH (ms)"
        value={data.minSpeechMs}
        onChange={v => data.onChange?.('minSpeechMs', v)}
        step={25} min={0}
        placeholder="150"
      />

      {/* Start-muted toggle — only applies when voxy is first started;
          the widget's mute button owns the live state after that. */}
      <label
        onMouseDown={e => e.stopPropagation()}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 12px 10px 12px',
          color: '#60d090', fontSize: 10, cursor: 'pointer',
        }}
      >
        <input className={nodeControlClass()}
          type="checkbox"
          checked={!!data.startMuted}
          onChange={e => data.onChange?.('startMuted', e.target.checked)}
          style={{ margin: 0 }}
        />
        <span style={{ letterSpacing: 0.5 }}>START MUTED</span>
      </label>
    </div>
  )
}
